/* eslint-disable kf/trans-tag-string */
// This file was not customer facing, so it was not translated.
import React, { useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

import { delayTime, getPlatformType } from "base/utils";

import { Queue } from "./queue.js";

const PRELOAD_QUEUE = new Queue();

/**
 * To store downloading status for dependent remotes
 */
window.loadingScripts = {};

async function initializeRemote(remote) {
  // Initializes the share scope. This fills it with known provided modules from this build and all remotes
  // eslint-disable-next-line no-undef
  await __webpack_init_sharing__("default");
  let container = window[remote];
  // Initialize the container, it may provide shared modules
  // eslint-disable-next-line no-undef
  await container.init(__webpack_share_scopes__.default);
  console.info(`Initializing remote module - ${remote}`);
}

/**
 * Function to initialise and get the remote module from loaded external.js
 *
 * @param {String} module Exported module path - Ex: "process/processHome"
 * @returns {Promise}     Promise object represents the exported remote module object
 */
function loadModule(module) {
  let [remote, ...moduleName] = module.split("/");
  // eslint-disable-next-line space-before-function-paren
  return async () => {
    await initializeRemote(remote);
    const factory = await window[remote].get(`./${moduleName.join("/")}`);
    const modules = factory();
    return modules;
  };
}

/**
 * Function to create external.js cloud asser url
 *
 * @param {String} remote Remote service name
 * @returns {String}      External js cloud asset url
 * @example
 * return /static/process/web/5851/external.js
 */
function constructAssetUrl(remote) {
  remote = remote.split("kf")[1] || remote;

  return `${window.publicPath}static/${remote}/${getPlatformType()}/${
    window.dependent_versionNumber[remote]
  }/external.js`;
}

/**
 * Function to create script tag with remote service external.js url, to download the remote service external.js
 *
 * @param {String} url Remote service external.js cloud asset url
 */
function downloadScript(url) {
  return new Promise((resolve) => {
    const element = document.createElement("script");

    element.src = url;
    element.type = "text/javascript";
    element.async = true;
    // eslint-disable-next-line space-before-function-paren
    element.onload = () => {
      console.info(`Dynamic Script Loaded:`, url);
      resolve();
    };

    element.onerror = () => {
      throw new Error(`Dynamic Script Error: ${url}`);
    };

    document.head.appendChild(element);
  });
}

/**
 * Function to download target remote
 *
 * @param {String} remote Name of the target remote service
 * @returns {undefined}
 */
async function downloadRemote(remote) {
  if (window[remote]) {
    return;
  }

  if (!window.loadingScripts[remote]) {
    let remoteUrl = constructAssetUrl(remote);
    // mark in loadingScripts
    window.loadingScripts[remote] = true;

    // download base remote
    await downloadScript(remoteUrl);

    // remove from loading after download done
    Reflect.deleteProperty(window.loadingScripts, remote);
  }
  // Note : We can invoke Lazyremote multiple time before downloading particular remote service, this delay helped us to avoid multiple recursive checks
  await delayTime(200);

  return downloadRemote(remote);
}

/**
 * Function to load exported module from remote services
 *
 * @param {String} module Exported path of remote module
 * @returns {Promise}     Promies object resolves with remote module
 * @example
 * lazyRemoteModule("kfprocess/processStore");
 */
async function lazyRemoteModule(module) {
  let [remote] = module.split("/");

  await downloadRemote(remote);

  return await loadModule(module)();
}

LazyRemote.propTypes = {
  /**
   * Remote module's service name
   */
  name: PropTypes.string,
  /**
   * Remote module's exported path
   */
  module: PropTypes.string,
  /**
   * Remote component's name
   */
  componentName: PropTypes.string
};

/**
 * Component for import remote components
 *
 * @component
 * @param {Object} restProps Props of remote component
 * @example
 * return(
 *  <LazyRemote
 *    name="process"
 *     module="kfprocess/processhome"
 *     componentName="ProcessHome"
 *   />)
 */
function LazyRemote(props) {
  // eslint-disable-next-line no-unused-vars
  let { name, module, componentName, ...restProps } = props;

  let [Component, setComponent] = useState(() => () => {
    console.info("Loading remote module");
    return null;
  });

  useEffect(
    function loadComponent() {
      lazyRemoteModule(module)
        .then((module) => {
          setComponent(() => module[componentName]);
        })
        .catch((err) => {
          // @ts-ignore
          setComponent(() => () => (
            <p>
              Failed to load external module
              <details>
                <summary>
                  <Trans>View more</Trans>
                </summary>
                <p>{err.toString()}</p>
              </details>
            </p>
          ));
        });
    },
    [module, componentName]
  );

  return <Component {...restProps} />;
}

function preloadRemoteModule(module) {
  PRELOAD_QUEUE.enqueue(lazyRemoteModule, module);
}

export { LazyRemote, lazyRemoteModule, preloadRemoteModule };
